<template>
  <div ref="container" class="button_container right">
    <i-button
      class="mass-action"
      v-for="action in massActions" :key="action.title"
      @click.prevent="action.handler"
      :title="action.label"
      :disabled="action.disabled">
      <i-icon :icon="action.icon" class="fa5"></i-icon>
      {{ action.label }}
    </i-button>

    <!-- Dropdown for smaller screens -->
    <i-dropdown class="dropdown">
      <template v-slot:trigger="{toggle, attrs}">
        <button
          @click="toggle"
          class="i-button btn i-simple-btn mass-actions"
          :aria-label="LANG.moreOptionsLabel"
          v-bind="attrs">
            <i class="fa5 fas fa-ellipsis-h"></i>
            <span class="sr-only">{{LANG.moreOptionsLabel}}</span>
        </button>
      </template>
      <ul class="dropdown-menu i-dropdown-menu block pull-right">
        <li
          class="dropdown-mass-action"
          v-for="action in massActions" :key="action.title"
          role="option"
          :aria-label="action.label">
          <a href="#"
            :class="{ 'i-state-disabled': action.disabled }"
            @click.stop.prevent="action.handler"
            @keydown.enter.stop.prevent="action.handler"
            :title="action.label">
            <i-icon :icon="action.icon" class="fa5"></i-icon>
            <span>{{ action.label }}</span>
          </a>
        </li>
      </ul>
    </i-dropdown>

    <form ref="form" method="post" :action="controller" >
      <input type="hidden" :value="finishController" name="finish_controller" />
      <input v-for="([name, value]) in params"
        :key="`${name}_${value}`"
        :name="name"
        :value="value"
        type="hidden" />
    </form>

    <i-modal :title="LANG.unpublishDocuments" v-model:show="showUnpublishConfirmation">
      {{ LANG.confirmUnpublishDocuments }}
      <template #footer>
        <i-button variant="secondary" @click.prevent="showUnpublishConfirmation=false">{{ LANG.cancel }}</i-button>
        <i-button variant="primary" @click.prevent="this.massAction('unpublish')">{{ LANG.unpublish }}</i-button>
      </template>
    </i-modal>
    <i-modal :title="LANG.publishDocuments" v-model:show="showPublishConfirmation">
      {{ LANG.confirmPublishDocuments }}
      <template #footer>
        <i-button variant="secondary" @click.prevent="showPublishConfirmation=false">{{ LANG.cancel }}</i-button>
        <i-button variant="primary" @click.prevent="this.massAction('publish')">{{ LANG.publish }}</i-button>
      </template>
    </i-modal>
    <ModalCopyPaste v-model:show="showPasteModal"/>
    <ModalChangeDates v-model:show="showDatesModal"/>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';
import { mapBrowserGetters } from '@/store';
import globals from '@/globals';
import { FinishController } from '@/store/modules/entity_browser/config';
import { uniqBy } from 'lodash';
import ModalCopyPaste from '@/components/browser/ModalCopyPaste.vue';
import ModalChangeDates from '@/components/browser/ModalChangeDates.vue';
import { showToast } from '@/utils/tools';

const { uri } = globals.I.Util;
const SessionId = globals.I.settings.session.id;

export const ControllerParamFormat = {
  publish: ({ nodeId, url }) => ({
    publish_job: `/publish${nodeId}/uri:${url || '_all_'}`,
  }),
  unpublish: ({ nodeId, url }) => ({
    publish_job: `/unpublish${nodeId}/uri:${url || '_all_'}`,
  }),
  delete: ({ nodeId, url }) => ({
    [`del${nodeId.replaceAll('/', '_')}:${url || ''}`]: 'on',
  }),
};

export default {
  components: {
    ModalCopyPaste,
    ModalChangeDates,
  },
  data() {
    return {
      action: 'publish',
      showUnpublishConfirmation: false,
      showPublishConfirmation: false,
      showPasteModal: false,
      showDatesModal: false,
      footerSize: 0,
    };
  },
  computed: {
    ...mapState(['LANG', 'browserMode']),
    ...mapState('clipboard', {
      copiedDocuments: (clipboard) => clipboard[SessionId].documents,
    }),
    ...mapBrowserGetters([
      'selectedDocuments',
      'selectedDocumentsCount',
      'isMasterDocumentSelected',
      'selectedTreeNode',
    ]),
    ...mapGetters(['checkACL']),
    massActionsConfig() {
      return {
        copy: {
          label: this.LANG.copy,
          icon: 'copy',
          disabled: !this.selectedDocumentsCount,
          handler: () => this.copy(),
          hidden: !this.checkPastePermissions,
        },
        paste: {
          label: this.pasteLabel,
          icon: 'paste',
          disabled: !(this.copiedDocuments.length && this.selectedTreeNode),
          handler: () => { this.showPasteModal = true; },
          hidden: !this.checkPastePermissions,
        },
        dates: {
          label: this.LANG.changeDates,
          icon: 'calendar',
          disabled: !this.selectedDocumentsCount,
          handler: () => { this.showDatesModal = true; },
          hidden: !this.checkDatesPermissions,
        },
        delete: {
          label: this.LANG.delete,
          icon: 'times',
          disabled: !this.selectedDocumentsCount,
          handler: () => this.massAction('delete'),
          hidden: false,
        },
        unpublish: {
          label: this.LANG.unpublish,
          icon: 'download',
          disabled: !this.selectedDocumentsCount,
          handler: () => { this.showUnpublishConfirmation = true; },
          hidden: false,
        },
        publish: {
          label: this.LANG.publish,
          icon: 'globe',
          disabled: !this.selectedDocumentsCount,
          handler: () => { this.showPublishConfirmation = true; },
          hidden: false,
        },
      };
    },
    massActions() {
      return Object.values(this.massActionsConfig).filter((action) => !action.hidden);
    },
    controller() {
      return uri(`/document/media/content/published/mass_action/${this.action}`);
    },
    finishController() {
      return FinishController[this.browserMode];
    },
    params() {
      const url = this.action === 'delete' ? '' : '_all_';

      const alterUrl = this.selectedDocuments.map((doc) => ({
        nodeId: doc.nodeId,
        url: this.isMasterDocumentSelected(doc.nodeId) ? url : doc.url,
      }));

      const uniqueSelected = uniqBy(alterUrl, (doc) => [doc.nodeId, doc.url].join());

      return uniqueSelected
        .map(ControllerParamFormat[this.action])
        .map(Object.entries)
        .flat();
    },
    checkDatesPermissions() {
      return this.checkACL('/data/meta/document_dates') && (this.checkACL('/document/publish_date') || this.checkACL('/document/expiry_date') || this.checkACL('/document/resubmission_date'));
    },
    checkPastePermissions() {
      return this.checkACL('/data/copy/check_restrictions') && (this.checkACL('/data/copy/paste'));
    },
    pasteLabel() {
      if (this.copiedDocuments.length <= 0) {
        return this.LANG.paste;
      }

      return `${this.LANG.paste} ${this.copiedDocuments.length}`;
    },
  },
  methods: {
    ...mapMutations('clipboard', ['copyDocuments']),
    massAction(action = 'publish') {
      this.action = action;
      this.$nextTick(() => {
        const { form } = this.$refs;
        form.submit();
      });
    },
    copy() {
      const clipboard = this.selectedDocuments.map((d) => ({
        nodeId: d.node_id,
        title: d.title,
        preview: d.preview_url,
      }));

      // Filter only unique nodeIds
      const filteredArr = clipboard.filter((elem, index, self) => index === self.findIndex((t) => t.nodeId === elem.nodeId));

      this.copyDocuments(filteredArr);
      showToast(`${filteredArr.length} ${this.LANG.documentsCopied}`, 0);
    },
  },
};
</script>

<style scoped lang='scss'>
.dropdown {
  display: none;
}
.dropdown-mass-action {
  display: none;
}
.dropdown-menu {
  bottom: 50px;
  top: auto;
}

@container footer (width < 700px) {
  .dropdown {
    display: inline-block;
  }
  .mass-action:nth-child(-n + 2) {
    display: none;
  }
  .dropdown-mass-action:nth-child(-n + 2) {
    display: block;
  }
}
@container footer (width < 600px) {
  .dropdown {
    display: inline-block;
  }
  .mass-action:nth-child(-n + 3) {
    display: none;
  }
  .dropdown-mass-action:nth-child(-n + 3) {
    display: block;
  }
}
@container footer (width < 500px) {
  .dropdown {
    display: inline-block;
  }
  .mass-action:nth-child(-n + 4) {
    display: none;
  }
  .dropdown-mass-action:nth-child(-n + 4) {
    display: block;
  }
}
@container footer (width < 400px) {
  .dropdown {
    display: inline-block;
  }
  .mass-action {
    display: none;
  }
  .dropdown-mass-action {
    display: block;
  }
}
</style>
